import { useState, useEffect } from "react";
import { Menu, MenuItem, Button } from "@mui/material";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { orderStatus } from "src/helper/GlobalVariables";

const statusOptions = orderStatus.map((status) => ({
  id: status,
  label: status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()), // Capitalizes words and replaces underscores with spaces
  value: status,
}));

const formatStatus = (status) =>
  status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()); // Reusable formatter

const DropdownStatus = ({ currentStatus, onUpdateStatus, order }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [availableOptions, setAvailableOptions] = useState([]);
  const [isOrderDatePassed, setIsOrderDatePassed] = useState(false);

  useEffect(() => {
    if (!order) {
      setAvailableOptions(statusOptions);
      return;
    }

    // Service orders only move between requested / completed / cancelled
    // (guide §5.3–§5.4). Taxi date-passed and driver-transition rules do not apply.
    if (order.orderType === "service") {
      setIsOrderDatePassed(false);
      setAvailableOptions(
        statusOptions.filter((option) =>
          ["requested", "completed", "cancelled"].includes(option.value)
        )
      );
      return;
    }

    // Check if order date and time has passed
    const orderDateTime = new Date(order.orderDateTime);
    const currentDateTime = new Date();
    const dateTimeHasPassed = orderDateTime < currentDateTime;

    // Set state for date passed check
    setIsOrderDatePassed(dateTimeHasPassed);

    // If order date and time has passed, only allow cancelled status
    if (dateTimeHasPassed) {
      setAvailableOptions(
        statusOptions.filter((option) => option.value === "cancelled")
      );
      return;
    }

    const hasDriver = Boolean(order.driver);

    if (!hasDriver) {
      setAvailableOptions(
        statusOptions.filter(
          (option) =>
            option.value === "scheduled" || option.value === "cancelled"
        )
      );
    } else {
      // Has driver: allow scheduled, cancelled, and request_accepted
      setAvailableOptions(
        statusOptions.filter(
          (option) =>
            option.value === "scheduled" ||
            option.value === "cancelled" ||
            option.value === "request_accepted"
        )
      );
    }
  }, [order]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (status) => {
    onUpdateStatus(status); // Call the mutation
    handleClose(); // Close the dropdown
  };

  // Check if we should render a static button instead of dropdown
  const shouldRenderStaticButton =
    isOrderDatePassed &&
    (currentStatus === "cancelled" || currentStatus === "completed");

  // Determine button style based on status
  const getButtonStyle = () => {
    const baseStyle = { whiteSpace: "nowrap" };

    if (isOrderDatePassed) {
      if (currentStatus === "cancelled") {
        return {
          ...baseStyle,
          backgroundColor: "red",
          color: "white",
          "&:hover": { backgroundColor: "darkred" },
        };
      }
      if (currentStatus === "completed") {
        return {
          ...baseStyle,
          backgroundColor: "green",
          color: "white",
          "&:hover": { backgroundColor: "darkgreen" },
        };
      }
    }

    return baseStyle;
  };

  return (
    <div>
      {shouldRenderStaticButton ? (
        // Static button for passed date with cancelled/complete status
        <Button variant="contained" style={getButtonStyle()} disabled={true}>
          {formatStatus(currentStatus)}
        </Button>
      ) : (
        // Regular dropdown for all other cases
        <>
          <Button
            onClick={handleClick}
            variant="contained"
            style={getButtonStyle()}
            endIcon={anchorEl ? <FiChevronUp /> : <FiChevronDown />}
          >
            {currentStatus ? formatStatus(currentStatus) : "Select Status"}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {availableOptions.map((status) => (
              <MenuItem
                key={status.id}
                selected={status.value === currentStatus}
                onClick={() => handleSelect(status.value)}
              >
                {status.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </div>
  );
};

export default DropdownStatus;
